/* ============================================================
   SERAFINA motion core — "fabric physics"
   One rAF clock. Springs. Scroll velocity. GPU-only writes.
   No libraries. Sleeps when idle. Respects reduced motion.
   ============================================================ */
(function () {
  'use strict';
  var RM = matchMedia('(prefers-reduced-motion: reduce)');
  var FINE = matchMedia('(hover: hover) and (pointer: fine)');
  if (RM.matches) { document.documentElement.classList.add('sfm-rm'); return; }
  var docEl = document.documentElement;
  docEl.classList.add('sfm-js');

  /* ---------- clock: single rAF, sleeps when idle ---------- */
  var tasks = new Set();
  var running = false, lastT = 0;
  var scr = { y: window.scrollY, py: window.scrollY, v: 0 };
  function wake() { if (!running) { running = true; lastT = performance.now(); requestAnimationFrame(tick); } }
  function tick(now) {
    var dt = Math.min(0.05, (now - lastT) / 1000) || 0.016; lastT = now;
    scr.py = scr.y; scr.y = window.scrollY;
    var raw = (scr.y - scr.py) / dt;                    /* px/s */
    scr.v += (raw - scr.v) * Math.min(1, dt * 12);      /* smoothed */
    var alive = false;
    tasks.forEach(function (t) { if (t(dt, now) !== false) alive = true; });
    if (Math.abs(scr.v) > 2) alive = true;
    if (alive) requestAnimationFrame(tick); else { running = false; scr.v = 0; }
  }
  addEventListener('scroll', wake, { passive: true });
  addEventListener('resize', wake, { passive: true });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) wake(); });

  /* ---------- critically-damped spring ---------- */
  function spring(k) {
    var s = { x: 0, v: 0, t: 0, k: k || 170, active: false };
    s.step = function (dt) {
      var c = 2 * Math.sqrt(s.k);
      var a = -s.k * (s.x - s.t) - c * s.v;
      s.v += a * dt; s.x += s.v * dt;
      if (Math.abs(s.x - s.t) < 0.05 && Math.abs(s.v) < 0.05) { s.x = s.t; s.v = 0; s.active = false; }
      return s.active;
    };
    s.to = function (t) { s.t = t; s.active = true; wake(); };
    return s;
  }

  /* ---------- reveals: velocity-aware fabric settle ---------- */
  var reveals = [];
  document.querySelectorAll('[data-sfm-reveal]').forEach(function (el, i) {
    var r = el.getBoundingClientRect();
    if (r.top > innerHeight * 0.92) el.classList.add('sfm-wait');
    reveals.push(el);
  });
  var io = new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      var el = e.target;
      /* entrance offset scales with scroll velocity: fast scroll = deeper settle */
      var vel = Math.min(1, Math.abs(scr.v) / 2400);
      el.style.setProperty('--sfm-dy', (18 + 26 * vel) + 'px');
      el.style.setProperty('--sfm-shear', (vel * 0.6).toFixed(3) + 'deg');
      var d = parseFloat(el.dataset.sfmDelay || 0);
      setTimeout(function () { el.classList.add('is-in'); el.classList.remove('sfm-wait'); }, d * 1000);
    });
  }, { rootMargin: '0px 0px -7% 0px' });
  reveals.forEach(function (el) { io.observe(el); });

  /* ---------- hero scroll choreography + image depth ---------- */
  var hero = document.querySelector('.sfh');
  if (hero) {
    var hImg = hero.querySelector('.sfh__media img');
    var hContent = hero.querySelector('.sfh__content');
    var hH = function () { return hero.offsetHeight || 1; };
    tasks.add(function () {
      var y = scr.y; if (y > hH() * 1.15) return false;
      var p = Math.min(1, y / hH());
      if (hImg) hImg.style.transform = 'translate3d(0,' + (y * 0.16).toFixed(1) + 'px,0) scale(' + (1 + p * 0.07).toFixed(4) + ')';
      if (hContent) { hContent.style.transform = 'translate3d(0,' + (y * 0.32).toFixed(1) + 'px,0)'; hContent.style.opacity = Math.max(0, 1 - p * 1.5).toFixed(3); }
      return true;
    });
  }

  /* ---------- inner-image depth parallax (mosaic, maison) ---------- */
  var plx = [];
  document.querySelectorAll('.sfm-plx').forEach(function (wrap) {
    var img = wrap.querySelector('img'); if (img) plx.push({ w: wrap, i: img, on: false });
  });
  if (plx.length) {
    var pio = new IntersectionObserver(function (es) { es.forEach(function (e) { var p = plx.find(function (x) { return x.w === e.target; }); if (p) p.on = e.isIntersecting; if (e.isIntersecting) wake(); }); }, { rootMargin: '12% 0px' });
    plx.forEach(function (p) { pio.observe(p.w); });
    tasks.add(function () {
      var any = false;
      for (var j = 0; j < plx.length; j++) {
        var p = plx[j]; if (!p.on) continue; any = true;
        var r = p.w.getBoundingClientRect();
        var t = (r.top + r.height / 2 - innerHeight / 2) / (innerHeight / 2 + r.height / 2); /* -1..1 */
        p.i.style.transform = 'translate3d(0,' + (t * -4.2).toFixed(2) + '%,0) scale(1.12)';
      }
      return any && Math.abs(scr.v) > 1;
    });
  }

  /* ---------- runway: inertial drag + velocity shear ---------- */
  document.querySelectorAll('[data-sfr-strip]').forEach(function (strip) {
    var cards = strip.children;
    var shear = spring(120); shear.x = 0;
    var lastL = strip.scrollLeft, lastTm = performance.now();
    strip.addEventListener('scroll', function () {
      var now = performance.now(), dt = Math.max(8, now - lastTm) / 1000;
      var v = (strip.scrollLeft - lastL) / dt; lastL = strip.scrollLeft; lastTm = now;
      shear.to(Math.max(-1, Math.min(1, v / 3200)));
    }, { passive: true });
    tasks.add(function (dt) {
      if (!shear.active && shear.x === 0) return false;
      var live = shear.step(dt);
      if (Math.abs(shear.t) > 0.001) shear.to(shear.t * 0.82); /* decay toward rest */
      var sk = shear.x * 2.4, sc = 1 - Math.abs(shear.x) * 0.012;
      for (var i = 0; i < cards.length; i++) cards[i].style.transform = 'skewX(' + (-sk).toFixed(3) + 'deg) scale(' + sc.toFixed(4) + ')';
      if (!live && Math.abs(shear.x) < 0.002) { for (var j = 0; j < cards.length; j++) cards[j].style.transform = ''; shear.x = 0; shear.t = 0; return false; }
      return true;
    });
    /* desktop pointer drag with momentum fling */
    if (FINE.matches) {
      var drag = null, fling = 0;
      strip.addEventListener('pointerdown', function (e) {
        if (e.button !== 0) return;
        drag = { x: e.clientX, l: strip.scrollLeft, v: 0, t: performance.now(), moved: false };
        fling = 0; strip.classList.add('sfm-drag');
      });
      addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        if (Math.abs(dx) > 4) drag.moved = true;
        var now = performance.now(), dt = Math.max(8, now - drag.t) / 1000;
        drag.v = drag.v * 0.7 + (-(dx) / dt) * 0.3 * 0.06;
        strip.scrollLeft = drag.l - dx;
      }, { passive: true });
      addEventListener('pointerup', function () {
        if (!drag) return;
        fling = drag.v * 16; drag = null; strip.classList.remove('sfm-drag');
        if (Math.abs(fling) > 1) { tasks.add(function fl(dt) {
          strip.scrollLeft += fling * dt * 60 / 16;
          fling *= Math.pow(0.0018, dt); /* exponential friction */
          if (Math.abs(fling) < 0.5) { tasks.delete(fl); return false; }
          return true;
        }); wake(); }
      }, { passive: true });
      strip.addEventListener('click', function (e) { if (strip.classList.contains('sfm-drag')) e.preventDefault(); }, true);
    }
  });

  /* ---------- magnetic controls ---------- */
  if (FINE.matches) document.querySelectorAll('[data-sfm-magnet]').forEach(function (el) {
    var sx = spring(200), sy = spring(200);
    var zone = el.parentElement;
    tasks.add(function (dt) {
      var a = sx.step(dt), b = sy.step(dt);
      el.style.transform = 'translate3d(' + sx.x.toFixed(2) + 'px,' + sy.x.toFixed(2) + 'px,0)';
      return a || b;
    });
    zone.addEventListener('pointermove', function (e) {
      var r = el.getBoundingClientRect();
      var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      var dx = e.clientX - cx, dy = e.clientY - cy;
      var d = Math.hypot(dx, dy), R = 110;
      if (d < R) { sx.to(dx * 0.32); sy.to(dy * 0.32); } else { sx.to(0); sy.to(0); }
    }, { passive: true });
    zone.addEventListener('pointerleave', function () { sx.to(0); sy.to(0); }, { passive: true });
  });

  /* ---------- pointer-weighted image pan on cards ---------- */
  if (FINE.matches) document.querySelectorAll('.sfr__card .sfr__img, .sfg__tile .sfg__img').forEach(function (frame) {
    var img = frame.querySelector('img'); if (!img) return;
    var sx = spring(150), sy = spring(150), sc = spring(150); sc.x = 1; sc.t = 1;
    var host = frame.closest('a') || frame;
    tasks.add(function (dt) {
      var a = sx.step(dt), b = sy.step(dt), c = sc.step(dt);
      if (a || b || c) img.style.transform = 'translate3d(' + sx.x.toFixed(2) + 'px,' + sy.x.toFixed(2) + 'px,0) scale(' + sc.x.toFixed(4) + ')';
      return a || b || c;
    });
    host.addEventListener('pointerenter', function () { sc.to(1.045); }, { passive: true });
    host.addEventListener('pointermove', function (e) {
      var r = frame.getBoundingClientRect();
      sx.to(((e.clientX - r.left) / r.width - 0.5) * 10);
      sy.to(((e.clientY - r.top) / r.height - 0.5) * 8);
    }, { passive: true });
    host.addEventListener('pointerleave', function () { sx.to(0); sy.to(0); sc.to(1); }, { passive: true });
  });

  /* ---------- directional underline CTAs ---------- */
  if (FINE.matches) document.querySelectorAll('.sfh__ctas a, .sfr__all, .sfm__cta').forEach(function (a) {
    a.classList.add('sfm-ul');
    a.addEventListener('pointerenter', function (e) {
      var r = a.getBoundingClientRect();
      a.classList.toggle('sfm-ul--r', e.clientX > r.left + r.width / 2);
    }, { passive: true });
  });

  /* ---------- page veil transitions ---------- */
  var veil = document.createElement('div');
  veil.id = 'sfm-veil'; veil.setAttribute('aria-hidden', 'true');
  veil.innerHTML = '<span>SERAFINA</span>';
  document.body.appendChild(veil);
  addEventListener('pageshow', function (e) { if (e.persisted) veil.classList.remove('is-on'); });
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest('a'); if (!a) return;
    var href = a.getAttribute('href') || '';
    if (a.target === '_blank' || !href || href[0] === '#' || /^(mailto|tel|javascript)/.test(href)) return;
    var url; try { url = new URL(href, location.href); } catch (_) { return; }
    if (url.origin !== location.origin) return;
    if (/\/(cart|checkout|account)/.test(url.pathname)) return;
    if (url.pathname === location.pathname && url.search === location.search) return;
    e.preventDefault();
    veil.classList.add('is-on');
    setTimeout(function () { location.href = url.href; }, 340);
  });

  wake();
})();
